import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Chip, Dialog, Divider, IconButton, Stack, TextField, Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ActionButton from "../../../component/common/ActionButton";
import FormModal from "../../../component/common/FormModal";
import LoadingDots from "../../../component/common/LoadingDots";
import WorkRequestTimeline from "./WorkRequestTimeline";
import { workRequestApi } from "../api/workRequestApi";
import { runApiAction } from "../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { formatDottedDate, formatDottedDateTimeMinute } from "../../../util/dateUtils";
import { availableActions, canSubmitDelivery, statusMeta } from "./workRequestConstants";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;

/**
 * 과업 요청서 상세 — <b>한 화면에서 모든 왕복이 끝나는 곳</b>.
 *
 * <p>헤더(상태·팀·담당자·기한) → 상태 액션 → 버전 타임라인(최신본 기본) → 피드백 스레드.
 * 기존 메일 왕복이 이 하나로 대체된다.
 *
 * <p>액션 버튼은 서버가 내려준 {@code isRequester / isAssignee} 와 현재 상태로 계산한다.
 * 화면 편의일 뿐이고 실제 강제는 서버가 다시 한다.
 */
export default function WorkRequestDetailModal({ open, requestId, myId, onClose, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    // 사유가 필요한 상태 전이(재오픈) — { action, reason }
    const [reasonPrompt, setReasonPrompt] = useState(null);

    const load = useCallback(() => {
        if (!requestId) return;
        setLoading(true);
        workRequestApi.get(requestId)
            .then(setDetail)
            .catch(() => setDetail(null))
            .finally(() => setLoading(false));
    }, [requestId]);

    useEffect(() => {
        if (!open) { setDetail(null); return; }
        load();
    }, [open, load]);

    /** 상세를 다시 읽고 목록도 갱신 — 상태·버전이 바뀌면 목록의 배지도 따라가야 한다. */
    const refresh = () => { load(); onChanged?.(); };

    /** 사유가 필요한 액션은 입력 모달을 먼저 띄운다(사유는 피드백 스레드에 그대로 남는다). */
    const changeStatus = (action) => {
        if (action.needsReason) { setReasonPrompt({ action, reason: "" }); return; }
        applyStatus(action, null);
    };

    const applyStatus = (action, reason) => runApiAction({
        request: () => workRequestApi.changeStatus(requestId, action.status, reason),
        setLoading: setBusy,
        successMsg: `${action.label} 처리했습니다.`,
        onSuccess: () => { setReasonPrompt(null); refresh(); },
    });

    const removeRequest = async () => {
        const ok = await confirmDialog({
            title: "과업 요청서 삭제",
            message: "요청서와 모든 버전·첨부·피드백이 함께 삭제됩니다.\n계속할까요?",
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        runApiAction({
            request: () => workRequestApi.remove(requestId),
            setLoading: setBusy,
            successMsg: "과업 요청서를 삭제했습니다.",
            onSuccess: () => { onClose?.(); onChanged?.(); },
        });
    };

    const meta = statusMeta(detail?.status);
    const actions = detail ? availableActions(detail) : [];
    // 요청자·담당자 둘 다 완료 전까지는 언제든 올릴 수 있다(접수 같은 관문 없음).
    const canUpload = Boolean(
        detail && (detail.isRequester || detail.isAssignee) && canSubmitDelivery(detail.status));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            // 좌우 2단(요청│응답) 타임라인이라 폭이 좁으면 파일명이 다 잘린다 — 화면을 넉넉히 쓴다.
            PaperProps={{ sx: { borderRadius: 2, width: "92vw", maxWidth: 1280, height: "92vh" } }}
        >
            {/* 헤더 */}
            <Box sx={{
                px: 2.5, py: 1.5, borderBottom: `1px solid ${C.border}`, bgcolor: "#fff",
                display: "flex", alignItems: "flex-start", gap: 1,
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.4 }}>
                        <Chip label={meta.label} size="small" sx={{
                            height: 20, fontSize: 11, fontWeight: 700,
                            bgcolor: meta.bg, color: meta.fg, border: `1px solid ${meta.border}`,
                        }} />
                        {detail && (
                            <Chip label={`v${detail.currentVersion}`} size="small" sx={{
                                height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#e2e8f0", color: C.textSecondary,
                            }} />
                        )}
                    </Stack>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }} noWrap>
                        {detail?.title || "과업 요청서"}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}><CloseRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
            </Box>

            {/* 본문 */}
            <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2, bgcolor: "#fff" }}>
                {loading && <LoadingDots />}
                {!loading && !detail && (
                    <Typography sx={{ fontSize: 13, color: C.textMuted }}>
                        요청서를 불러올 수 없습니다. 열람 권한이 없거나 삭제된 요청일 수 있습니다.
                    </Typography>
                )}

                {!loading && detail && (
                    <>
                        <Box sx={{
                            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: 1.2, p: 1.5, mb: 2, borderRadius: 1.5, bgcolor: "#f8fafc",
                            border: `1px solid ${C.border}`,
                        }}>
                            <Meta label="요청 팀"   value={detail.targetDeptName} />
                            <Meta label="요청 유형" value={detail.requestTypeName} />
                            <Meta label="요청자"    value={detail.requesterName} />
                            <Meta label="담당자"    value={detail.assigneeName} />
                            <Meta label="희망 완료일" value={detail.dueDate ? formatDottedDate(detail.dueDate) : "-"} />
                            <Meta label="등록일"    value={formatDottedDateTimeMinute(detail.createdAt)} />
                        </Box>

                        {detail.content && (
                            <Box sx={{ mb: 2 }}>
                                <SectionTitle>요청 내용</SectionTitle>
                                <Typography sx={{ fontSize: 12.5, color: C.textPrimary, whiteSpace: "pre-wrap" }}>
                                    {detail.content}
                                </Typography>
                            </Box>
                        )}

                        {/* 지금 내가 할 수 있는 일 — 눈에 먼저 들어오도록 강조 배경에 담는다.
                            (삭제는 여기 섞지 않는다. 진행 액션과 파괴적 액션이 나란히 있으면 오클릭이 난다) */}
                        {actions.length > 0 && (
                            <Box sx={{
                                p: 1.5, mb: 2, borderRadius: 1.5,
                                bgcolor: C.primaryBg, border: `1px solid ${C.primaryBorder}`,
                            }}>
                                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.primaryText, mb: 1 }}>
                                    지금 할 수 있는 작업
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {actions.map(a => (
                                        <ActionButton
                                            key={a.status}
                                            variant={a.variant}
                                            size="small"
                                            loading={busy}
                                            onClick={() => changeStatus(a)}
                                        >
                                            {a.label}
                                        </ActionButton>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* 버전과 그 버전에 달린 대화를 한 블록으로 — 피드백을 맨 아래 몰아두면
                            v2·v3 가 쌓였을 때 어느 버전 얘기인지 알 수 없다. 최신이 맨 아래(입력창 바로 위). */}
                        <SectionTitle>진행 상황</SectionTitle>
                        <WorkRequestTimeline
                            requestId={requestId}
                            revisions={detail.revisions}
                            comments={detail.comments}
                            myId={myId}
                            requesterId={detail.requesterId}
                            isAssignee={detail.isAssignee}
                            canWrite={canUpload}
                            onChanged={refresh}
                        />

                        {/* 위험 영역 — 되돌릴 수 없는 삭제는 맨 아래에, 진행 액션과 멀리 떼어 놓는다. */}
                        {detail.isRequester && (
                            <>
                                {/* 경고 문구는 두지 않는다 — 확인 다이얼로그가 이미 같은 말을 한다. */}
                                <Divider sx={{ mt: 3, mb: 1.5 }} />
                                <Stack direction="row" justifyContent="flex-end">
                                    <ActionButton variant="delete" size="small" loading={busy}
                                                  onClick={removeRequest} />
                                </Stack>
                            </>
                        )}
                    </>
                )}
            </Box>

            {/* 수정요청·재오픈 사유 입력 — 사유는 서버가 피드백 스레드에 함께 기록한다. */}
            <FormModal
                open={Boolean(reasonPrompt)}
                onClose={() => setReasonPrompt(null)}
                title={`${reasonPrompt?.action.label || ""} 사유`}
                subtitle="입력한 사유는 피드백에 기록되어 담당자가 바로 확인합니다."
                maxWidth="xs"
                saving={busy}
                saveLabel={reasonPrompt?.action.label || "확인"}
                saveDisabled={!reasonPrompt?.reason.trim()}
                onSave={() => applyStatus(reasonPrompt.action, reasonPrompt.reason.trim())}
            >
                <TextField
                    fullWidth multiline minRows={3} size="small" autoFocus
                    value={reasonPrompt?.reason || ""}
                    onChange={(e) => setReasonPrompt(p => ({ ...p, reason: e.target.value }))}
                    placeholder="예: 로고 위치를 우측 하단으로 옮겨주세요."
                />
            </FormModal>
        </Dialog>
    );
}

function Meta({ label, value }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: C.textMuted, mb: 0.2 }}>{label}</Typography>
            <Typography sx={{ fontSize: 12.5, color: C.textPrimary }} noWrap>{value || "-"}</Typography>
        </Box>
    );
}

function SectionTitle({ children }) {
    return (
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary, mb: 1 }}>
            {children}
        </Typography>
    );
}
